import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/axios';

// Shared by Login and Signup — both render a GoogleSignInButton and need the same
// verify-then-branch behavior: existing account logs straight in, new email routes to
// the onboarding-completion form (the raw idToken travels via router state, not the URL).
// `plan` (optional) is the plan picked on the pricing/signup page, carried through so
// onboarding can prefill it — Login has none, and the backend already defaults to STARTER.
export default function useGoogleAuthFlow(plan) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('login');
  const [googleError, setGoogleError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleCredential = useCallback(async (idToken) => {
    setGoogleError('');
    setGoogleLoading(true);
    try {
      const res = await apiClient.post('/api/auth/google', { idToken });
      if (res.data?.needsOnboarding) {
        navigate('/google-onboarding', {
          state: { idToken, email: res.data.email, fullName: res.data.fullName, plan },
        });
        return;
      }
      if (res.data?.token) {
        auth.login(res.data.token);
        navigate('/dashboard');
      }
    } catch (err) {
      if (err.response) {
        setGoogleError(err.response.data?.message || t('error.googleFailed'));
      } else {
        setGoogleError(t('error.networkError'));
      }
    } finally {
      setGoogleLoading(false);
    }
  }, [auth, navigate, t, plan]);

  return { handleGoogleCredential, googleError, googleLoading };
}
